/**
 * 根据接口返回的 menus 过滤本地路由树
 * @param {Array} localRoutes - 本地全量路由配置
 * @param {Array} apiMenus - 接口返回的 menus 数组（包含 path 和 menuName）
 * @returns {Array} 过滤后的路由树
 */
export function filterMenuByPath(localRoutes, apiMenus) {
  // 校验数据有效性
  if (!localRoutes || !Array.isArray(localRoutes)) {
    console.warn('filterMenuByPath: localRoutes is not an array')
    return []
  }
  if (!apiMenus || !Array.isArray(apiMenus)) {
    console.warn('filterMenuByPath: apiMenus is not an array')
    return []
  }

  // 递归提取所有叶子节点的路径（只有最深层级的菜单才应该显示）
  const leafPaths = new Set()

  function extractLeafPaths(menus, parentPath = '') {
    if (!menus || !Array.isArray(menus)) return

    menus.forEach(menu => {
      if (menu.path) {
        const normalizedPath = menu.path.startsWith('/') ? menu.path : '/' + menu.path
        // 如果有 children 且 children 不为空，递归处理
        if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
          extractLeafPaths(menu.children, normalizedPath)
        } else {
          // 没有子菜单或者子菜单为空，说明是叶子节点
          leafPaths.add(normalizedPath)
          // 也添加不带斜杠的版本
          leafPaths.add(menu.path)
        }
      }
    })
  }

  extractLeafPaths(apiMenus)

  // 调试日志：查看提取的叶子节点
  console.log('叶子节点路径:', Array.from(leafPaths))

  // 递归创建 path 到 menuName 的映射（支持多层级）
  const pathToMenuNameMap = {}

  function buildPathMap(menus) {
    if (!menus || !Array.isArray(menus)) return

    menus.forEach(menu => {
      if (menu.path) {
        const normalizedPath = menu.path.startsWith('/') ? menu.path : '/' + menu.path
        // 如果是叶子节点（没有子菜单或子菜单为空），加入映射
        const isLeafNode = !menu.children || (Array.isArray(menu.children) && menu.children.length === 0)
        if (isLeafNode) {
          pathToMenuNameMap[normalizedPath] = menu.menuName
          pathToMenuNameMap[menu.path] = menu.menuName
        }
        // 递归处理子菜单
        if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
          buildPathMap(menu.children)
        }
      }
    })
  }
  buildPathMap(apiMenus)
  // 记录已匹配的菜单 ID，避免重复匹配
  const matchedPaths = new Set()

  /**
   * 递归过滤路由树
   * @param {Array} routes - 当前层级的路由
   * @param {String} parentPath - 父级路由的完整路径
   * @returns {Array} 过滤后的路由
   */
  function filterRecursive(routes, parentPath = '') {
    const result = []

    if (!routes || !Array.isArray(routes)) {
      return result
    }

    routes.forEach(route => {
      // 深拷贝避免修改原数据
      const filteredRoute = { ...route }
      // 构建当前路由的完整路径
      const currentFullPath = route.path.startsWith('/')
        ? route.path
        : (parentPath ? parentPath + '/' + route.path : '/' + route.path)
      // 检查当前路由是否在接口返回的 paths 中（支持多种匹配方式）
      // 优先级：完整路径 > 相对路径
      let matchedKey = null
      // 首先尝试匹配完整路径（更精确）
      const fullPathKeys = Object.keys(pathToMenuNameMap).filter(key =>
        key === currentFullPath || key === '/' + currentFullPath.replace(/^\//, '')
      )
      if (fullPathKeys.length > 0 && !matchedPaths.has(currentFullPath)) {
        // 优先使用完整路径匹配
        matchedKey = fullPathKeys.find(key => !matchedPaths.has(key))
      }
      // 如果完整路径没有匹配，再尝试相对路径
      if (!matchedKey) {
        matchedKey = Object.keys(pathToMenuNameMap).find(key => {
          // 排除已经匹配过的路径
          if (matchedPaths.has(key)) return false
          // 完全匹配
          if (key === route.path) return true
          // 完整路径匹配
          if (key === currentFullPath) return true
          // 去掉开头斜杠匹配
          if (key === route.path.replace(/^\//, '')) return true
          // 带斜杠匹配
          if (key === '/' + route.path) return true
          return false
        })
      }
      if (matchedKey) {
        matchedPaths.add(matchedKey)
        matchedPaths.add(currentFullPath)
      }
      // 递归处理子路由（无论当前路由是否匹配）
      let hasMatchingChild = false
      if (filteredRoute.children && filteredRoute.children.length > 0) {
        const filteredChildren = filterRecursive(filteredRoute.children, currentFullPath)
        if (filteredChildren.length > 0) {
          filteredRoute.children = filteredChildren
          hasMatchingChild = true
        } else {
          // 如果没有匹配的子路由，清空 children
          filteredRoute.children = []
        }
      }
      // 如果当前路由匹配了，或者是有匹配子路由的父节点，则添加到结果中
      if (matchedKey) {
        // 使用接口的 menuName 更新 meta.title
        if (!filteredRoute.meta) {
          filteredRoute.meta = {}
        }
        filteredRoute.meta.title = pathToMenuNameMap[matchedKey]
        result.push(filteredRoute)
      } else if (hasMatchingChild) {
        // 当前路由没有匹配，但有匹配的子路由，保留作为容器（不更新 title）
        result.push(filteredRoute)
      }
    })

    return result
  }

  return filterRecursive(localRoutes)
}
